import { useCallback, useEffect, useRef, useState } from 'react'
import BackOfficeAPIClient from "../../../clients/BackofficeAPIClient"
import { RequiredFieldStateCode } from "../../../enums/EtatChampObligatoire"
import { ChampsObligatoiresReferentiel, ConfigChampObligatoire } from "../../../types/ChampsObligatoires"

type EtatsParChamp = Record<number, RequiredFieldStateCode>

interface UseChampsObligatoiresConfigReturn {
    referentiel: ChampsObligatoiresReferentiel | null
    loading: boolean
    saving: boolean
    hasChanges: boolean
    changesCount: number
    getEtat: (templateFieldId: number) => RequiredFieldStateCode | undefined
    updateEtat: (templateFieldId: number, state: RequiredFieldStateCode) => void
    saveChanges: () => Promise<boolean>
    discardChanges: () => void
    reload: () => Promise<void>
}

/**
 * Charge le référentiel (BDREF3) et la config déjà enregistrée (base tenant), les fusionne,
 * et ne renvoie à la sauvegarde que les champs sur lesquels l'utilisateur a réellement cliqué
 * (même si la valeur choisie est identique à la valeur par défaut) : la base tenant ne doit
 * contenir que des choix explicites.
 */
const useChampsObligatoiresConfig = (): UseChampsObligatoiresConfigReturn => {
    const apiClient = new BackOfficeAPIClient()

    const [referentiel, setReferentiel] = useState<ChampsObligatoiresReferentiel | null>(null)
    const [loading, setLoading] = useState<boolean>(true)
    const [saving, setSaving] = useState<boolean>(false)
    const [etats, setEtats] = useState<EtatsParChamp>({})
    const [touched, setTouched] = useState<Set<number>>(new Set())
    const baseline = useRef<EtatsParChamp>({})

    const fetchData = useCallback(async () => {
        setLoading(true)
        try {
            const [referentielData, configData] = await Promise.all([
                apiClient.get<ChampsObligatoiresReferentiel>('/champsObligatoires/referentiel'),
                apiClient.get<ConfigChampObligatoire[]>('/champsObligatoires/config'),
            ])

            const configByTemplateFieldId = new Map(configData.map(c => [c.templateFieldId, c.state]))
            const initialEtats: EtatsParChamp = {}

            for (const context of referentielData.contexts) {
                for (const bloc of context.blocks) {
                    for (const champ of bloc.fields) {
                        if (champ.isCore) continue
                        initialEtats[champ.templateFieldId] = configByTemplateFieldId.get(champ.templateFieldId) ?? champ.defaultState
                    }
                }
            }

            setReferentiel(referentielData)
            setEtats(initialEtats)
            baseline.current = initialEtats
            setTouched(new Set())
        } catch (error) {
            console.log(error)
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        fetchData()
    }, [fetchData])

    const getEtat = useCallback((templateFieldId: number) => etats[templateFieldId], [etats])

    const updateEtat = useCallback((templateFieldId: number, state: RequiredFieldStateCode) => {
        setEtats(prev => ({ ...prev, [templateFieldId]: state }))
        setTouched(prev => new Set(prev).add(templateFieldId))
    }, [])

    const saveChanges = useCallback(async () => {
        if (touched.size === 0) return true

        setSaving(true)
        try {
            const changes = Array.from(touched).map(templateFieldId => ({
                templateFieldId,
                state: etats[templateFieldId],
            }))

            await apiClient.put('/champsObligatoires/config', { changes })

            baseline.current = { ...etats }
            setTouched(new Set())
            return true
        } catch (error) {
            console.log(error)
            throw error
        } finally {
            setSaving(false)
        }
    }, [touched, etats])

    const discardChanges = useCallback(() => {
        setEtats(baseline.current)
        setTouched(new Set())
    }, [])

    return {
        referentiel,
        loading,
        saving,
        hasChanges: touched.size > 0,
        changesCount: touched.size,
        getEtat,
        updateEtat,
        saveChanges,
        discardChanges,
        reload: fetchData,
    }
}

export default useChampsObligatoiresConfig
